import { ajax } from 'jquery';
import * as React from 'react';
import { urlValidationsPath } from '../../../../javascript/application/ts_routes';
import { useCustomEventEffect } from '../../hooks';
import UrlStatusIcon from '../urlInput/UrlStatusIcon';
import { HiddenInputs } from './shared/HiddenInputs';
import {
  IAnswerMatchParams,
  IQuestionAnswerUpdateEventDetails,
  IQuestionDetails,
  matchQuestionConditions,
} from './shared/utilities';

interface IProps {
  initialInputValue: string;
  name: string;
  placeholder: string;
  questionDetails: IQuestionDetails;
  conditionalQuestionParentQuestionId: string;
  conditionalQuestionConditions: { operator: string; value: string }[];
  formResourceType: 'application' | 'job_seeker';
}

export default function Url(props: IProps) {
  const {
    initialInputValue,
    name,
    placeholder,
    questionDetails,
    conditionalQuestionConditions,
    conditionalQuestionParentQuestionId,
    formResourceType,
  } = props;
  const { index, questionId, questionType, title, additionalInformation, required } = questionDetails;
  const condition = conditionalQuestionConditions && conditionalQuestionConditions[0];
  const [visible, setVisible] = React.useState<boolean>(conditionalQuestionParentQuestionId === null);
  const [inputValue, setInputValue] = React.useState<string>(initialInputValue || '');
  const [isValid, setIsValid] = React.useState<boolean>(null);
  const [showValidationMessage, setShowValidationMessage] = React.useState<boolean>(false);

  /**
   * Sender - announce to possible conditional question answers when the value of this rendered component changes
   */
  const { dispatch: announceAnswerUpdate } = useCustomEventEffect(`question:${questionId}:answer:update`);

  React.useEffect(() => {
    setTimeout(() => {
      announceAnswerUpdate({ questionType: 'url', value: inputValue });
    }, 250);
  }, []);

  /**
   * Receiver - receive announcements from possible parent conditional questions about their changing value(s) so
   * we know whether to hide or show this answer component
   */
  useCustomEventEffect(
    `question:${conditionalQuestionParentQuestionId}:answer:update`,
    ({ detail }: CustomEvent<IQuestionAnswerUpdateEventDetails>) => {
      const matchParams: IAnswerMatchParams = {
        questionType: detail.questionType,
        value: detail.value,
        parentQuestionCondition: condition,
        maxNumAnswerOptions: detail.maxNumAnswerOptions,
      };
      // Only hide element if it's got a parent conditional question
      if (conditionalQuestionParentQuestionId) {
        setVisible(matchQuestionConditions(matchParams));
      }
    },
  );

  function handleInputChange(event: React.FormEvent<HTMLInputElement>) {
    setInputValue(event.currentTarget.value);
    announceAnswerUpdate({ questionType: 'url', value: event.currentTarget.value });
  }

  function handleValidationMessage() {
    if (!isValid) {
      return <div className="mar-t-1 text-warning animated animate__fadeIn text-right">Unreachable URL</div>;
    }
  }

  function handleDisplayValidationMessage() {
    return showValidationMessage && handleValidationMessage();
  }

  function inputStyles() {
    if (showValidationMessage) {
      const color = isValid ? '#3c763d' : '#FFC526';
      return { border: `1px solid ${color}` };
    }
  }

  function formatUrl(url) {
    let formattedUrl = url;
    if (url && !/^(f|ht)tps?:\/\//i.test(url)) {
      formattedUrl = `https://${url}`;
    }
    return formattedUrl;
  }

  function handleBlur() {
    if (!inputValue) {
      setShowValidationMessage(false);
      return;
    }
    const websiteUrl = inputValue;
    const formattedUrl = formatUrl(websiteUrl);
    handleUrlValidation(formattedUrl);
    setInputValue(inputValue);
  }

  function handleUrlValidation(websiteUrl: string) {
    ajax({
      cache: false,
      data: { url: websiteUrl },
      dataType: 'json',
      success: (responseCode) => {
        setIsValid(responseCode === 200);
        setShowValidationMessage(true);
      },
      type: 'GET',
      url: urlValidationsPath(),
    });
  }

  if (!visible) return null;

  return (
    <div className="pad-v-3">
      <label
        className="external-form__label"
        htmlFor={`${formResourceType}_form_${formResourceType}_answers_attributes_${index}_url_answer`}
      >
        <span className={`external-form__label--title ${required ? 'external-form__label--required' : ''}`}>
          {title}
        </span>
        {additionalInformation && <span>{additionalInformation}</span>}
      </label>
      <div className="frow frow--gutters frow--direction-column frow--row-tn frow--items-tn-center frow--nowrap">
        <div className="col-flex-grow-1">
          <div style={{ position: 'relative' }}>
            {showValidationMessage && <UrlStatusIcon isValid={isValid} />}
            <input
              id={name}
              type="text"
              name={name}
              placeholder={placeholder}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              style={inputStyles()}
              required={required}
            />
          </div>
        </div>
      </div>
      {handleDisplayValidationMessage()}
      <HiddenInputs
        formResourceType={formResourceType}
        index={index}
        additionalInformation={additionalInformation}
        title={title}
        questionId={questionId}
        questionType={questionType}
      />
    </div>
  );
}

Url.defaultProps = {
  changeInput: true,
};
